/**
 *  * *******************************************************************************************************
 *
 * V1.2.4
 *
 * User: tomers
 * Date: 8/27/13
 *
 * Changes:
 * Added a new permutations "BLUELINK" that takes its css class from the _default.scss file - .bluelink.
 *
 * **********************************************************************************************************
 *
 * V1.2.3
 *
 * User: liors
 * Date: 8/11/13
 * Visual: BTN_SPECIAL permutations (+ icon in front of the text with no defined width)
 *
 * Changes:
 * Added a new permutations "BTN_SPECIAL" that has no defined width.
 *
 * **********************************************************************************************************
 *  * *******************************************************************************************************
 *
 * V1.2.2
 *
 * User: omarg
 * Date: 5/12/13
 * Visual: no change
 *
 * Changes:
 * Adding tabIndex functionality to the Button.
 *
 * **********************************************************************************************************
 *
 * V1.2.1
 *
 * User: tomers
 * Date: 4/22/13
 * Visual PDF: https://drive.google.com/a/liveperson.com/?tab=mo#folders/0BzCszeQVU73ycVZXS0RYQkwxd28
 *
 * Changes:
 *
 * Replace type and size attributes with 'permutation' attribute.
 * As for now we support 5 permutations (BTN / BTN_PRIMARY / LINK / BTN_SPECIAL_SMALL / BTN_SPECIAL_XXLARGE ).
 * Options:
 * permutation - optional. One of the options in the static Button.PERMUTATION enum. PERMUTATION.BTN is the default
 *
 *
 * **********************************************************************************************************

 * V1.2
 *
 * User: tomers
 * Date: 3/20/13
 * Visual PDF: https://docs.google.com/a/liveperson.com/file/d/0B7O_LAPhjkbkV2kwYXRwbGc5c3c/edit
 *
 * This widget is used to implement a button on the page.
 * Currently we support 3 types (default, primary and link) and 2 states (enabled and disabled).
 * For default and primary buttons we are using jquery-ui-button in order to implement the button, but for link button we are creating an <a> element with no need in jquery-ui-button
 * Options:
 * el - mandatory. jQuery element. The element on the page where the button will be created.
 * text - optional. string. Should be the text in the 'el' object (to support i18n multi language), but can be sent as part of the options object
 * type - optional. One of the options in the static Button.TYPE enum (BTN / BTN_PRIMARY / LINK). BTN is the default
 * initState -  optional. The init state of the button, one of the options in the static Button.STATES enum (ENABLED / DISABLED). ENABLED is the default
 * onClick - optional. A reference to a callback function that will be called when click on the function is triggered
 * tooltip - optional. String. Appears as tooltip when the cursor is hover the button. If the tooltip is not sent we are using the text as the tooltip.
 * size - optional.One of the options in the static Button.SIZE enum (SMALL / XXLARGE). SMALL is the default
 * Using Example:
 *  var addCampaignBtn = new LPButton({
 *      el : this.ui.addCampaignBtn,
 *      type : LPButton.TYPE.BTN_PRIMARY,
 *      initState: LPButton.STATES.ENABLED,
 *      onClick : function() { alert('clicked!') }
 *  });
 *  addCampaignBtn.render();
 *
 * *******************************************************************************************************
 */
define(function (require) {
    "use strict";

    var $ = require('jquery');
    var Logger = require('logger');
    var Marionette = require('marionette');
    var $widget = require('jqueryui/widget');
    var $button = require('jqueryui/button');
    var _ = require("underscore");
    var Bubble = require("ui/bubble/Bubble");

    var DOUBLE_CLICK_INTERVAL = 500; //in milliseconds

    /**
     * @class Button Component
     * @type {*}
     */
    var Button = Marionette.ItemView.extend({
        withUIComponents:true,
        initialize:function (options) {
            this.logger = Logger.getLogger('ButtonWidget');
            this.text = options.text || this.$el.text();
            this.tooltip = _.has(options, "tooltip") ? options.tooltip : "";
            this.tooltipAlign = _.has(options, "tooltipAlign") ? options.tooltipAlign : Button.TOOLTIP_ALIGN.MIDDLE;
            this.permutation = options.permutation || Button.PERMUTATION.BTN;
            this.tabindex = options.tabindex || this.$el.attr("data-tabindex") || "";
            this.state = options.initState || Button.STATES.ENABLED;
            this.onClick = options.onClick;
            this._isJQueryButton = (this.permutation != Button.PERMUTATION.LINK &&
                                    this.permutation != Button.PERMUTATION.BLUELINK &&
                                    this.permutation != Button.PERMUTATION.LIGHT_BLUELINK &&
                                    this.permutation != Button.PERMUTATION.BLUELINK_TEXT_ALIGN &&
                                    this.permutation != Button.PERMUTATION.BLUELINK_SMALL &&
                                    this.permutation != Button.PERMUTATION.ORANGELINK &&
                                    this.permutation != Button.PERMUTATION.LINK_NIGHT);
            this._button = null;
            this.errorMsg = null;
            this.errorTooltipAlignment = Bubble.ALIGN.MIDDLE;
            this.errorPosition = options.errorPosition || Button.ERROR_POSITION.BOTTOM;
            this.lastTimeClicked = 0; // used to prevent double click.
        },

        /**
         * creates the button HTML element
         * if we need to create a link button we are creating the <a> element on the page and not using jquery ui button
         * if we need to create a real button we are using jquery ui button
         */
        render:function () {
            var that = this;
            var classByPermutation = this._getButtonClass();

            if (this._isJQueryButton) {
                this._button = $("<button>").attr('type', 'button').addClass(classByPermutation);
                this._button.button({ label:this.text, text:true });

            } else {
                this._button = $("<a>").attr('href', 'void(0)').addClass(classByPermutation);
                this._button.text(this.text);
                this._button.addClass((this.state != Button.STATES.ENABLED) ? "disabled" : "");
            }
            this._button.addClass((this.state != Button.STATES.ENABLED) ? "disabled" : "");
            this._button.attr("tabindex", this.tabindex);
            this._button.attr("title", this.tooltip);
            this._button.attr("tooltip-align", this.tooltipAlign);
            this._button.click(function (event) {
                // In case the button is a link button, trigger blur event after click to make it behave consistently
                // across browsers. Otherwise clicking ENTER after clicking a link triggers another click event in IE
                // and FF.
                if (!that._isJQueryButton) {
                    that._button.blur();
                }

                var currentTimestamp = (new Date()).getTime();
                event.preventDefault();
                //event.stopPropagation(); // removed - should not stop propagation of click event in the button level!

                // before call the onclick function, check for enabled state and that onclick function exists
                // also, calculate the time so the callback doesnt run twice. we trigger the callback once in the double click interval
                if ((that.state == Button.STATES.ENABLED) && (currentTimestamp - that.lastTimeClicked > DOUBLE_CLICK_INTERVAL)) {
                    if (that.onClick && typeof(that.onClick) == 'function') {
                        try {
                            that.onClick(event);
                        }
                        catch (e) {
                            that.logger.error("onClick callback threw an error - type: " + e.name + " msg: " + e.message,"Button:click");
                            that.logger.graph({ name: "UIComponents:Button:OnclickException"});
                        }
                    }

                    that.closeBubble();

                    that.trigger("click");
                }

                that.lastTimeClicked = currentTimestamp;
            });

            this._button.focus(_.bind(this.openBubble, this));
            this._button.blur(_.bind(this.closeBubble, this));
            this._button.mouseenter(_.bind(this.openBubble, this));
            this._button.mouseleave(_.bind(this.closeBubble, this));

            this.$el.html(this._button);

            this.bindUIElements(); // because we override the render method we should call this method manually

            return this;
        },

        /**
         * returns the correct button class by its permutation (default class is no class)
         * @return {String} - the relevant class to attach to the button element on the page
         * @private
         */
        _getButtonClass:function () {
            return Button.PERMUTATION_CLASSNAMES[this.permutation] || "";
        },

        setText: function (text){
            if (this._isJQueryButton) {
                this._button.button( "option", "label", text );
            } else {
                this._button.text(text);
            }
        },

        /**
         * set the state of the button to enabled/disabled
         * @param {Button.STATES} state - the new state - taken from Button.STATES
         */
        setState:function (state) {
            this.state = state || Button.STATES.ENABLED;
            this._button[(this.state == Button.STATES.DISABLED) ? "addClass" : "removeClass"]("disabled");
        },

        /**
         * Helper function for enabling and disabling the button with TRUE/FALSE parameter
         * @param enabled
         */
        setEnabled: function(enabled) {
            var state = (enabled) ? Button.STATES.ENABLED : Button.STATES.DISABLED;
            this.setState(state);
        },
        setTooltip: function(tooltip, align) {
            this.tooltip = tooltip;
            this.tooltipAlign = align;
            this._updateTooltip();
        },

        _updateTooltip: function() {
            this._button.attr("title", this.tooltip);
            // tooltip-text (following line) is a patch for an exception in which the tooltip is kept opened while modification is done
            this._button.attr("tooltip-text", this.tooltip);
            if (!_.isUndefined(this.tooltipAlign)) {
                this._button.attr("tooltip-align", this.tooltipAlign);
            }
        },

        /**
         * @return {Button.STATES} - the current state of the button - taken form Button.STATES
         */
        getState:function () {
            return this.state;
        },

        /**
         * toggle between the states of the button (disabled->enabled ; enabled->disabled)
         * @return {Button.STATES} - the new state of the button
         */
        toggleState:function () {
            this.setState(Button.STATES.ENABLED == this.state ? Button.STATES.DISABLED : Button.STATES.ENABLED);

            return this.getState();
        },

        setError:function (isError, msg, align) {
            if (isError === true) {
                this.errorMsg = msg;
                this._button.addClass("error");
            }
            else {
                this._button.removeClass("error");
                this.errorMsg = null;
                this.closeBubble();
            }
            if (!_.isUndefined(align)) {
                this.errorTooltipAlignment = align;
            }
        },

        closeBubble: function(){
            //uiComponents can be undefined when you click on a button and on the click there is listener that closes the view.
            // In this case, the "mouseleave" will be triggered and fired after the close of the view the uiComponents set to null in this case.
            if (this.uiComponents && this.uiComponents.ButtonError) {
                this.uiComponents.ButtonError.close();
            }
        },

        openBubble: function() {
            if (this._button.hasClass("error") && this.errorMsg && !this.uiComponents.ButtonError) {
                this.uiComponents.host$.createAndRender("ButtonError", {
                    type:Bubble,
                    options:{
                        targetControl:this._button,
                        text:this.errorMsg,
                        position:this.errorPosition,
                        icon:Bubble.ICON.ERROR,
                        showCloseButton:false,
                        align: this.errorTooltipAlignment
                    }
                });
            }
        },

        /**
         * triggers click on the button
         */
        click:function () {
            this._button.trigger("click");
        },

        display:function() {
            this.$el.show.apply(this.$el, [].slice.apply(arguments));
        },

        hide:function() {
            this.$el.hide.apply(this.$el, [].slice.apply(arguments));
        },

        /**
         * triggered when the view is close
         */
        onClose:function () {
            // Custom cleanup or closing code
            if (this._button) {
                if (this._isJQueryButton) {
                    this._button.button('destroy');
                }
                this._button.off("focus");
                this._button.off("blur");
                this._button.off("mouseenter");
                this._button.off("mouseleave");
                this._button.empty();
                this._button.remove();
            }
        }
    });

    Button.ERROR_POSITION = Bubble.POSITION;

    // Public ENUM to control
    // The permutation of the button
    Button.PERMUTATION = {
        BTN:1,
        BTN_PRIMARY:2,
        LINK:3,
        BTN_SPECIAL_SMALL:4,
        BTN_SPECIAL_XXLARGE:5,
        BTN_SPECIAL_FULL:6,
        BTN_SPECIAL: 7,
        BLUELINK: 8,
        TEXT_ALIGN_LEFT:9,
		BTN_SHORT:10,
		BTN_NEXT :11,
		BTN_PREV :12,
        BTN_PRIMARY_MARKETING:13,
        BTN_LOCK:14,
        ORANGELINK: 15,
        BTN_PLUS: 16,
        BTN_DARK: 17,
        //BTN_APPLY: 18,
        BTN_REFRESH: 19,
        BTN_SHORT_ADD: 20,
        LINK_NIGHT: 21,
        BTN_NIGHT: 22,
        BTN_PLUS_NIGHT: 23,
        LIGHT_BLUELINK: 24,
        BTN_HEIGHT_ALIGN: 25,
        BLUELINK_TEXT_ALIGN: 26,
        BLUELINK_SMALL: 27,
        BTN_ORGANIZE: 28
    };

    Button.PERMUTATION_CLASSNAMES = _.invert({
        'btn-primary primary': Button.PERMUTATION.BTN_PRIMARY,
        'btn-link': Button.PERMUTATION.LINK,
        'special btn-small': Button.PERMUTATION.BTN_SPECIAL_SMALL,
        'special btn-xxlarge': Button.PERMUTATION.BTN_SPECIAL_XXLARGE,
        'special btn-full': Button.PERMUTATION.BTN_SPECIAL_FULL,
        'special': Button.PERMUTATION.BTN_SPECIAL,
        'bluelink': Button.PERMUTATION.BLUELINK,
        'light-bluelink': Button.PERMUTATION.LIGHT_BLUELINK,
        'bluelink-text-align': Button.PERMUTATION.BLUELINK_TEXT_ALIGN,
        'bluelink-small': Button.PERMUTATION.BLUELINK_SMALL,
        'btn-text-align-left': Button.PERMUTATION.TEXT_ALIGN_LEFT,
        'btn-short': Button.PERMUTATION.BTN_SHORT,
        'btn-short-add': Button.PERMUTATION.BTN_SHORT_ADD,
        'btn-next': Button.PERMUTATION.BTN_NEXT,
        'btn-prev': Button.PERMUTATION.BTN_PREV,
        'btn-primary primary btn_marketing': Button.PERMUTATION.BTN_PRIMARY_MARKETING,
        'btn-primary primary btn-lock': Button.PERMUTATION.BTN_LOCK,
        "orangelink": Button.PERMUTATION.ORANGELINK,
        'btn-plus': Button.PERMUTATION.BTN_PLUS,
        'btn-dark': Button.PERMUTATION.BTN_DARK,
        'btn-apply': Button.PERMUTATION.BTN_APPLY,
        'btn-refresh': Button.PERMUTATION.BTN_REFRESH,
        'btn-link-night': Button.PERMUTATION.LINK_NIGHT,
        'btn-night': Button.PERMUTATION.BTN_NIGHT,
        'btn-plus-night': Button.PERMUTATION.BTN_PLUS_NIGHT,
        'btn-height-align': Button.PERMUTATION.BTN_HEIGHT_ALIGN,
        'btn-organize primary': Button.PERMUTATION.BTN_ORGANIZE
    });

    // Public ENUM to control
    // The state of the button
    Button.STATES = {
        ENABLED:1,
        DISABLED:2
    };

    Button.TOOLTIP_ALIGN = {
        MIDDLE: "middle",
        LEFT: "left",
        RIGHT: "right",
        TOP: "top",
        BOTTOM: "bottom"
    };

    return Button;
});
